/**
 * Case link carried into Workbench writes that leave the canvas.
 *
 * C-03: the Instagram OSINT "push to canvas" hand-off stores the case its leads
 * came from; the Workbench now opens on that case.
 * C-06: watchlist adds made from a Workbench opened for a case carry the case.
 */
import { watchlistService } from '../services/api';

export const INSTAGRAM_PUSH_KEY = 'instagram_graph_push';
/** A push older than this is ignored (the officer left the hand-off). */
export const INSTAGRAM_PUSH_MAX_AGE_MS = 10 * 60 * 1000;

export interface InstagramPush {
    savedAt: number;
    caseId: string | null;
    elements: any[];
}

/** Workbench URL for the Instagram hand-off, on the case the leads belong to. */
export function instagramWorkbenchUrl(caseId: string | null | undefined): string {
    const params = new URLSearchParams({ source: 'instagram_osint' });
    const id = (caseId || '').trim();
    if (id) params.set('case_id', id);
    return `/workbench?${params.toString()}`;
}

/** The stored hand-off, or null when absent, corrupt, empty or stale. */
export function parseInstagramPush(raw: string | null | undefined, now: number = Date.now()): InstagramPush | null {
    if (!raw) return null;
    let push: any;
    try {
        push = JSON.parse(raw);
    } catch {
        return null;
    }
    if (!push || !Array.isArray(push.elements) || push.elements.length === 0) return null;
    const savedAt = Number(push.savedAt);
    if (!Number.isFinite(savedAt) || now - savedAt >= INSTAGRAM_PUSH_MAX_AGE_MS) return null;
    const caseId = typeof push.caseId === 'string' && push.caseId.trim()
        ? push.caseId.trim()
        : (typeof push.caseId === 'number' ? String(push.caseId) : null);
    return { savedAt, caseId, elements: push.elements };
}

/**
 * Which case the pushed leads open on. The URL's case wins when present; the
 * push's case is adopted into the URL when the URL has none.
 */
export function instagramPushCase(
    urlCaseId: string | null | undefined,
    pushCaseId: string | null | undefined,
): { caseId: string | null; adoptIntoUrl: boolean; mismatch: boolean } {
    const url = (urlCaseId || '').trim() || null;
    const pushed = (pushCaseId || '').trim() || null;
    if (url) return { caseId: url, adoptIntoUrl: false, mismatch: !!pushed && pushed !== url };
    return { caseId: pushed, adoptIntoUrl: !!pushed, mismatch: false };
}

/** POST /watchlist with the Workbench's case (`case_id` omitted when there is none). */
export function addToWatchlistForCase(
    address: string,
    chain: string,
    label: string | undefined,
    caseId: string | null | undefined,
) {
    return watchlistService.add(address, chain, label, undefined, caseId || undefined);
}
